import { ContractualMonth, MonthLookup, OffsetLookup } from "@shared/models";
import { CONTRACTUAL_MONTHS } from "@shared/misc";

/**
 * @description Gets the contractual month object based on the month number.
 * @param {MonthLookup} lookup - Wrapper {@link MonthLookup} containing month as a number.
 * @returns {ContractualMonth} - {@link ContractualMonth} object.
 * @example getContractualMonth({ month: 1 }); // Returns January.
 */
export function getContractualMonth(lookup: MonthLookup): ContractualMonth; // Overload for direct month lookup

/**
 * @description Gets the contractual month object based on the current month and an offset.
 * @param {OffsetLookup} lookup - Wrapper {@link OffsetLookup} containing number of months to offset from the current month.
 * @returns {ContractualMonth} - {@link ContractualMonth} object.
 * @example getContractualMonth({ offset: 0 }); // Returns the current month.
 */
export function getContractualMonth(lookup: OffsetLookup): ContractualMonth; // Overload for offset lookup

export function getContractualMonth(lookup: MonthLookup | OffsetLookup): ContractualMonth {
  const today = new Date();
  const year = today.getFullYear();

  if ("month" in lookup) {
    // Direct month lookup
    return CONTRACTUAL_MONTHS.find((contractualMonth) => parseInt(contractualMonth.asInt) === lookup.month)!;
  } else {
    // Offset lookup
    const currentMonthIndex = CONTRACTUAL_MONTHS.findIndex((month) => {
      const start = new Date(
        `${year}-${month.start.month.padStart(2, "0")}-${month.start.day.padStart(2, "0")}T00:00:00`
      );
      const end = new Date(`${year}-${month.end.month.padStart(2, "0")}-${month.end.day.padStart(2, "0")}T23:59:59`);
      return today >= start && today <= end;
    });

    const desiredMonthIndex =
      (currentMonthIndex + lookup.offset + CONTRACTUAL_MONTHS.length) % CONTRACTUAL_MONTHS.length;
    return CONTRACTUAL_MONTHS[desiredMonthIndex];
  }
}
